import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAtom } from 'jotai';
import { Button } from 'syf-component-library/ui/atoms';
import { Grid } from 'syf-component-library/ui/patterns';
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing';
import { Body, Small } from 'syf-component-library/ui/typography';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE, MESSAGE_TYPES, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import MainContext from '../../../../Context';
import { handleMultiPaymentMutation, sendResultToMultiPaymentFlow } from '../../../../hooks/syf-api/payments/multiPaymentMutation';
import { useConversionCount } from '../../../../store/conversionCountAtom';
import { MakeAPaymentView, useCurrentViewAtom } from '../../../../store/currentViewAtom';
import { multiPaymentStateAtom, useMultiPaymentQueryAtoms } from '../../../../store/multiPaymentAtoms';
import { t } from '../../../../strings/i18n';
import { AsyncModalFooter } from '../../../../ui/atoms/styled';
import { FAR_FILE_ALT, FAR_PEN } from '../../../../const/iconProps';
import Disclaimer from '../../../atoms/Disclaimer';
import IntroHeading from '../../../atoms/IntroHeading';
import PaymentStepper from '../../../molecules/PaymentModalStates/PaymentFormSubcomponents/PaymentStepper';
import { PaymentDetailsHeader } from '../../../molecules/PaymentsDetailsGrid';
import MultiPaymentList from '../MultiPaymentFlow/PaymentCardList';
import { MultiPaymentFlowWrapper } from '../MultiPaymentFlow/subcomponents';
import { FooterWrapperWithZIndex, SettlementReviewEditWrapper } from './subcomponents';
const MultiPaymentReview = ({ handleCancel }) => {
    const [loading, setLoading] = useState(false);
    const { apiConfig } = useContext(MainContext);
    const [, currentViewReducer] = useCurrentViewAtom();
    const [conversionCount, setConversionCount] = useConversionCount();
    const { triggerAnalyticsEvent } = useContext(MainContext);
    const [multiPaymentState, setMultiPaymentState] = useAtom(multiPaymentStateAtom);
    const { paymentMethodsQueryResult: { data: paymentMethods } } = useMultiPaymentQueryAtoms();
    const handleEditButton = () => {
        currentViewReducer({
            type: 'updateView',
            view: MakeAPaymentView.MultiPaymentDetailsEdit
        });
    };
    const handleSubmitHandler = async () => {
        try {
            setLoading(true);
            const payments = Object.values(multiPaymentState);
            const responses = [];
            for (let i = 0; i < payments.length; i += 1) {
                const payment = payments[i];
                // eslint-disable-next-line no-await-in-loop
                const { body, status } = await handleMultiPaymentMutation(apiConfig, payment, paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.paymentMethods);
                const { paymentId, hasPaymentError } = sendResultToMultiPaymentFlow({
                    paymentBody: body,
                    paymentStatus: status
                });
                if (!hasPaymentError)
                    setConversionCount(conversionCount + 1);
                responses.push(Object.assign(Object.assign({}, payment), { paymentConfirmation: paymentId }));
            }
            const newMultiPaymentState = responses.reduce((accumulator, value, index) => {
                accumulator[index] = value;
                return accumulator;
            }, {});
            setMultiPaymentState(newMultiPaymentState);
            currentViewReducer({
                type: 'updateView',
                view: MakeAPaymentView.MultiPaymentConfirmation
            });
        }
        catch (e) {
            currentViewReducer({
                type: 'updateView',
                view: MakeAPaymentView.MultiPaymentError
            });
            throw e;
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const eventOptions = {
            messageType: MESSAGE_TYPES.DCH.SETTLEMENT_MULTI,
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.MULTI_PAYMENT,
            pageName: PAGE_NAME.MULTI_PAYMENT.REVIEW
        };
        triggerAnalyticsEvent(eventOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs(MultiPaymentFlowWrapper, { children: [_jsx(PaymentStepper, { step: "review" }), _jsx(Stack, { size: "large" }), _jsx(IntroHeading, { children: t('text.reviewPaymentAlmostDone') }), _jsx(Stack, { size: "large" }), _jsx(PaymentDetailsHeader, { dataCy: "payment-details-heading", text: "multiPaymentFlow.scheduledPayment" }), _jsxs("div", { children: [_jsx(Body, { children: t('makePayment.paymentType') }), _jsxs(SettlementReviewEditWrapper, { children: [_jsx(Body, Object.assign({ weight: "bold" }, { children: t('multiPaymentFlow.multiPayment') })), _jsx("div", { children: _jsxs(Button, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-reason": DATA_REASON.MULTI_PAYMENT.EDIT, "data-type": DATA_TYPE.MAKE_A_PAYMENT, onClick: handleEditButton }, { children: [_jsx(FontAwesomeIcon, { icon: FAR_PEN, size: "1x" }), _jsx(Inline, { size: "small" }), t('button.edit')] })) })] })] }), _jsx(MultiPaymentList, {}), _jsx(Grid, Object.assign({ container: true }, { children: _jsx(Disclaimer, { dataCy: "authorize-payment-disclaimer", headingText: t('legal.paymentDisclosure'), iconFAText: FAR_FILE_ALT }) })), _jsx(Inset, Object.assign({ top: "small" }, { children: _jsx(Small, Object.assign({ "data-cy": "authorize-payment-disclaimer-text" }, { children: t('legal.authorizePaymentCondition', {
                                shouldEnrollInOneClickAutopay: t('legal.authorizePayments')
                            }) })) }))] }), _jsx(FooterWrapperWithZIndex, { children: _jsx(AsyncModalFooter, { dataCy: "payment-review-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: DATA_REASON.MULTI_PAYMENT.AUTHORIZE_PAYMENTS, dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: DATA_REASON.MULTI_PAYMENT.CANCEL, dataType: DATA_TYPE.MAKE_A_PAYMENT, handlePrimaryButton: handleSubmitHandler, handleSecondaryButton: handleCancel, isPrimaryLoading: loading, primaryText: t('legal.authorizePayments'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) })] }));
};
export default MultiPaymentReview;
